import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { LineSeries } from "@nivo/line";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCompanyKeyMetrics } from "../hooks/useCompanyKeyMetrics";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialLineChart from "./shared/GenericFinancialLineChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";

dayjs.extend(quarterOfYear);

const RETURN_CATEGORIES = [
  {
    key: "roe",
    label: "Return on Equity",
    color: "#22c55e",
    field: "returnOnEquity" as const,
  },
  {
    key: "roic",
    label: "Return on Invested Capital",
    color: "#3b82f6",
    field: "returnOnInvestedCapital" as const,
  },
  {
    key: "roce",
    label: "Return on Capital Employed",
    color: "#f59e0b",
    field: "returnOnCapitalEmployed" as const,
  },
];

const categoryColorMap: Record<string, string> = RETURN_CATEGORIES.reduce(
  (acc, cat) => ({ ...acc, [cat.label]: cat.color }),
  {} as Record<string, string>,
);

interface ReturnMetricsChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const ReturnMetricsChart: React.FC<ReturnMetricsChartProps> = ({
  ticker,
  height = 260,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyKeyMetrics(ticker, maxLimit, period);

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(RETURN_CATEGORIES.map((cat) => cat.key)),
  );

  const chartData = useMemo((): LineSeries[] => {
    if (!data || data.length === 0) return [];

    const filteredData =
      effectiveViewMode === "annual"
        ? data.filter((item) => item.period === "FY")
        : data.filter((item) => ["Q1", "Q2", "Q3", "Q4"].includes(item.period));

    const sortedData = [...filteredData].sort(
      (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    );

    return RETURN_CATEGORIES.filter((cat) =>
      visibleCategories.has(cat.key),
    ).map((category) => ({
      id: category.label,
      color: category.color,
      data: safeSlice(
        sortedData
          .filter((item) => item[category.field] != null)
          .map((item) => ({
            x:
              effectiveViewMode === "annual"
                ? dayjs(item.date).year().toString()
                : `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`,
            y: (item[category.field] ?? 0) * 100,
          })),
        effectiveLimit,
      ),
    }));
  }, [data, visibleCategories, effectiveViewMode, effectiveLimit]);

  const isEmpty =
    !chartData.length || chartData.every((series) => series.data.length === 0);

  const toggleCategory = (categoryKey: string) => {
    setVisibleCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        if (next.size > 1) next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const chipLegend = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2, justifyContent: "center" }}>
      {RETURN_CATEGORIES.map((category) => (
        <ChartToggleButton
          key={category.key}
          label={category.label}
          selected={visibleCategories.has(category.key)}
          onToggle={() => toggleCategory(category.key)}
          color={category.color}
        />
      ))}
    </Box>
  );

  const tooltipRenderer = (point: {
    data: { xFormatted: string | number; y: number };
    serieId: string | number;
    serieColor: string;
  }) => {
    const rows = RETURN_CATEGORIES.filter((cat) =>
      visibleCategories.has(cat.key),
    ).flatMap((category) => {
      const matchingPoint = chartData
        .find((s) => s.id === category.label)
        ?.data.find((d) => String(d.x) === String(point.data.xFormatted));
      if (!matchingPoint) return [];
      return [{ label: category.label, value: `${(matchingPoint.y as number)?.toFixed(1) ?? "0.0"}%`, color: category.color }];
    });

    return <ChartTooltip header={String(point.data.xFormatted)} rows={rows} />;
  };

  return (
    <GenericFinancialLineChart
      title="Return Metrics"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error ?? null}
      chartData={chartData}
      isEmpty={isEmpty}
      enableCrosshair={false}
      lineWidth={3.5}
      axisTickFontSize={13}
      bottomMargin={60}
      yAxisLegend="Return (%)"
      yAxisFormat={(value) => `${Number(value).toFixed(0)}%`}
      colors={(serie: LineSeries) => categoryColorMap[serie.id as string] ?? "#22c55e"}
      tooltip={tooltipRenderer}
      belowChartContent={chipLegend}
      noDataTitle="No Return Metrics Data Available"
      noDataDescription="Return on equity, invested capital, and capital employed data is not available for this company."
    />
  );
};

export default ReturnMetricsChart;
